import {head} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {isDefined} from 'shared/web';

import {
  AuditAssetsUploadV2ApiArg,
  AuditAssetsUploadV2ApiResponse,
  CloneInspectionApiArg,
  CreateAuditApiArg,
  CreateAuditApiResponse,
  CreateAutoFillTemplateApiArg,
  CreateAutoFillTemplateApiResponse,
  CreateInspectionApiArg,
  CreateInspectionApiResponse,
  CreateTypeOfControlSettingsApiArg,
  CreateTypeOfControlSettingsApiResponse,
  DeleteAutoFillTemplateApiArg,
  DeleteAutoFillTemplateApiResponse,
  DeleteInspectionApiArg,
  DeleteInspectionApiResponse,
  DeleteInspectionTemplateApiArg,
  DeleteInspectionTemplateApiResponse,
  DeleteVideoAssetApiArg,
  FileOperationListApiArg,
  FileOperationListApiResponse,
  GetAuditDataApiArg,
  GetAuditDataApiResponse,
  GetAuditStructureApiArg,
  GetAuditStructureApiResponse,
  GetAutoFillTemplateApiArg,
  GetAutoFillTemplateApiResponse,
  GetAutoFillTemplatesApiArg,
  GetAutoFillTemplatesApiResponse,
  FileStorageUploadV2ApiArg,
  FileStorageUploadV2ApiResponse,
  fileStorageUploadV2ApiResponseSchema,
  GetBasicStructureForSettingsApiArg,
  GetBasicStructureForSettingsApiResponse,
  GetInspectionEquipmentDocumentApiArg,
  GetInspectionEquipmentDocumentApiResponse,
  getInspectionEquipmentDocumentApiResponseSchema,
  GetMultipleAuditDataApiArg,
  GetMultipleAuditDataApiResponse,
  GetNewestStructureByTypeApiArg,
  GetNewestStructureByTypeApiResponse,
  GetPublicizeInspectionApiArg,
  GetPublicizeInspectionApiResponse,
  GetSpecificallyAuditDataApiArg,
  GetSpecificallyAuditDataApiResponse,
  GetStructureSettingsApiArg,
  GetStructureSettingsApiResponse,
  GetVehicleAuditApiArg,
  GetVideoAssetApiArg,
  GetVideoAssetApiResponse,
  InspectionListApiArg,
  inspectionListApiArgSchema,
  InspectionListApiResponse,
  InspectionTemplatesApiResponse,
  InspectionToInProgressApiArg,
  InspectionType,
  LoadAuditDataByVehicleIdApiArg,
  LoadAuditDataByVehicleIdApiResponse,
  MoveInspectionDataApiArg,
  MoveInspectionDataApiResponse,
  RemoveInspectionEquipmentDocumentApiArg,
  RemoveInspectionEquipmentDocumentApiResponse,
  ReplaceInspectionEquipmentDocumentApiArg,
  ReplaceInspectionEquipmentDocumentApiResponse,
  RotateImageAssetApiArg,
  RotateImageAssetApiResponse,
  SyncInspectionToConditionApiArg,
  syncInspectionToConditionApiArgSchema,
  ToFinishingInspectionApiArg,
  ToFinishingInspectionApiResponse,
  ToggleInspectionTemplateActiveStateApiArg,
  ToggleInspectionTemplateActiveStateApiResponse,
  ToInRevisionInspectionApiArg,
  ToInRevisionInspectionApiResponse,
  ToRevisionInspectionApiArg,
  ToRevisionInspectionApiResponse,
  UpdateAuditConditionDataApiArg,
  UpdateAuditConditionDataApiResponse,
  UpdateAuditDataApiArg,
  UpdateAuditDataApiResponse,
  UpdateInspectionStructureApiArg,
  UploadVideoAssetApiArg,
  UploadVideoAssetApiResponse,
  VehicleAuditStatusLogs,
} from './types';

export const carAuditApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createAutoFillTemplate: build.mutation<
      CreateAutoFillTemplateApiResponse,
      CreateAutoFillTemplateApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v1/inspection/${queryArg.vehicleId}`,
          method: 'POST',
          body: {
            inspectionType: InspectionType.AUTOFILL_TEMPLATE,
            auditStructureId: queryArg.auditStructureId,
            inspectionName: queryArg.inspectionName,
          },
        };
      },
      invalidatesTags: ['AutoFillTemplates'],
    }),
    getAutoFillTemplates: build.query<GetAutoFillTemplatesApiResponse, GetAutoFillTemplatesApiArg>({
      query: () => ({
        url: `/car-audit/v1/inspections`,
        method: 'POST',
        body: {
          vehicleId: null,
          inspectionType: InspectionType.AUTOFILL_TEMPLATE,
        },
      }),
      transformResponse: (response: GetAutoFillTemplatesApiResponse) =>
        response.filter((item) => item.inspectionName !== null),
      providesTags: ['AutoFillTemplates'],
    }),
    deleteAutoFillTemplate: build.mutation<
      DeleteAutoFillTemplateApiResponse,
      DeleteAutoFillTemplateApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v1/inspection/${queryArg.inspectionId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['AutoFillTemplates'],
    }),
    getAuditStructure: build.query<GetAuditStructureApiResponse, GetAuditStructureApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure/${queryArg.auditStructureId}`,
        method: 'GET',
      }),
    }),
    getNewestStructureByType: build.query<
      GetNewestStructureByTypeApiResponse,
      GetNewestStructureByTypeApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure/${queryArg.inspectionType}/newest`,
        method: 'GET',
      }),
    }),
    updateAuditData: build.mutation<UpdateAuditDataApiResponse, UpdateAuditDataApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/autofill-template/${queryArg.inspectionId}/${queryArg.autofillTemplateId}`,
        method: 'PUT',
        body: queryArg.requestBody,
      }),
    }),
    getAuditData: build.query<GetAuditDataApiResponse, GetAuditDataApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}`,
        method: 'GET',
      }),
    }),
    getMultipleAuditData: build.query<GetMultipleAuditDataApiResponse, GetMultipleAuditDataApiArg>({
      queryFn: async (queryArg, _queryApi, _extraOptions, fetchWithBQ) => {
        const results = await Promise.all(
          queryArg.auditIds.map(async (auditId) => {
            const result = await fetchWithBQ({
              url: `/car-audit/v1/audits/${auditId}`,
              method: 'GET',
            });
            if (result.error) {
              return [];
            }
            return result.data as GetAuditDataApiResponse;
          })
        );
        return {data: results.flat()};
      },
    }),
    getAutoFillTemplate: build.query<GetAutoFillTemplateApiResponse, GetAutoFillTemplateApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/autofill-template/${queryArg.inspectionId}/${queryArg.autoFillTemplateId}`,
        method: 'GET',
      }),
      transformResponse: (response: GetAutoFillTemplateApiResponse[]) => response[0],
    }),
    createAudit: build.mutation<CreateAuditApiResponse, CreateAuditApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v2/audits/${queryArg.vehicleId}`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    toFinishingInspection: build.mutation<
      ToFinishingInspectionApiResponse,
      ToFinishingInspectionApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v2/audits/${queryArg.inspectionId}/to_finishing`,
          method: 'PUT',
          body: {
            note: queryArg.note,
            updatedAt: null,
          },
        };
      },
      invalidatesTags: invalidateOnSuccess(['AuditData']),
    }),
    toRevisionInspection: build.mutation<
      ToRevisionInspectionApiResponse,
      ToRevisionInspectionApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v2/audits/${queryArg.inspectionId}/to_revision`,
          method: 'PUT',
          body: {
            updatedAt: null,
          },
        };
      },
      invalidatesTags: invalidateOnSuccess(['AuditData']),
    }),
    toInRevisionInspection: build.mutation<
      ToInRevisionInspectionApiResponse,
      ToInRevisionInspectionApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v2/audits/${queryArg.inspectionId}/in_revision`,
        method: 'PUT',
        body: queryArg.toInRevisionInspectionRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['AuditData']),
    }),
    inspectionToInProgress: build.mutation<void, InspectionToInProgressApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.inspectionId}/in_progress`,
        method: 'PUT',
      }),
    }),
    getInspectionTemplates: build.query<InspectionTemplatesApiResponse, {isActive?: boolean}>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure-templates${isDefined(queryArg.isActive) ? `?isActive=${queryArg.isActive}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: InspectionTemplatesApiResponse) =>
        response.filter((template) => template.key !== null),
      providesTags: ['InspectionTemplates'],
    }),
    deleteInspectionTemplate: build.mutation<
      DeleteInspectionTemplateApiResponse,
      DeleteInspectionTemplateApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v1/inspection-structure/${queryArg.templateId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['InspectionTemplates'],
    }),
    toggleInspectionTemplateActiveState: build.mutation<
      ToggleInspectionTemplateActiveStateApiResponse,
      ToggleInspectionTemplateActiveStateApiArg
    >({
      query(queryArg) {
        return {
          url: `/car-audit/v1/inspection-structure/${queryArg.templateId}/toggle-active-state`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, _arg) => (!isDefined(error) ? ['InspectionTemplates'] : []),
    }),
    getBasicStructureForSettings: build.query<
      GetBasicStructureForSettingsApiResponse,
      GetBasicStructureForSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure-settings/structure`,
        params: {templateId: queryArg.templateId, inspectionType: queryArg.inspectionType},
      }),
    }),
    createTypeOfControlSettings: build.mutation<
      CreateTypeOfControlSettingsApiResponse,
      CreateTypeOfControlSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure-settings`,
        method: 'POST',
        body: queryArg.createTypeOfControlSettingsRequestBody,
      }),
      invalidatesTags: ['InspectionTemplates'],
    }),
    getStructureSettings: build.query<GetStructureSettingsApiResponse, GetStructureSettingsApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audit-structure-settings/${queryArg.typeOfControlId}`,
      }),
    }),
    getPublicizeInspection: build.query<
      GetPublicizeInspectionApiResponse,
      GetPublicizeInspectionApiArg
    >({
      query({inspectionId}) {
        return {
          url: `/car-audit/v1/publicize-inspection/${inspectionId}`,
        };
      },
    }),
    deleteInspection: build.mutation<DeleteInspectionApiResponse, DeleteInspectionApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/inspection/${queryArg.inspectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'InspectionList', vehicleId: queryArg.force},
      ],
    }),
    inspectionList: build.query<InspectionListApiResponse, InspectionListApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/inspections`,
        method: 'POST',
        body: queryArg.inspectionListRequestBody,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'InspectionList', vehicleId: queryArg.inspectionListRequestBody.vehicleId},
      ],
      extraOptions: {
        requestSchema: inspectionListApiArgSchema,
      },
    }),
    createInspection: build.mutation<CreateInspectionApiResponse, CreateInspectionApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/inspection/${queryArg.vehicleId}`,
        method: 'POST',
        body: queryArg.createAuditRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'InspectionList', vehicleId: queryArg.vehicleId},
      ],
    }),
    loadAuditDataByVehicleId: build.query<
      LoadAuditDataByVehicleIdApiResponse,
      LoadAuditDataByVehicleIdApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/search/audits/${queryArg.inspectionType}`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getSpecificallyAuditData: build.query<
      GetSpecificallyAuditDataApiResponse,
      GetSpecificallyAuditDataApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/search/audit-data/${queryArg.inspectionType}`,
        method: 'POST',
        body: queryArg.specificallyAuditDataRequestBody,
      }),
      providesTags: (result, error, queryArg) => [
        {
          type: 'AuditData',
          id: head(queryArg.specificallyAuditDataRequestBody.vehicleIds ?? []),
        },
      ],
    }),
    rotateImageAsset: build.mutation<RotateImageAssetApiResponse, RotateImageAssetApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/images/rotate`,
        method: 'POST',
        body: queryArg.rotateImageAssetRequestBody,
      }),
    }),
    fileOperationList: build.query<FileOperationListApiResponse, FileOperationListApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/file-storage/file-operation`,
        params: {
          sourceFileId: queryArg.sourceFileId,
          sourceRemoteFileId: queryArg.sourceRemoteFileId,
          state: queryArg.state,
          batchId: queryArg.batchId,
        },
      }),
    }),
    getVehicleAuditApiCatalogs: build.query<VehicleAuditStatusLogs | null, GetVehicleAuditApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/vehicle-audit/${queryArg.auditId}/status-log`,
      }),
    }),
    getVideoAsset: build.query<GetVideoAssetApiResponse, GetVideoAssetApiArg>({
      query: ({auditId, videoAssetId}) => ({
        url: `/car-audit/v1/audits/${auditId}/video-asset/${videoAssetId}`,
        method: 'GET',
      }),
    }),
    uploadVideoAsset: build.mutation<UploadVideoAssetApiResponse, UploadVideoAssetApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}/video-asset`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    deleteVideoAsset: build.mutation<void, DeleteVideoAssetApiArg>({
      query: ({auditId, videoAssetId}) => ({
        url: `/car-audit/v1/audits/${auditId}/video-asset/${videoAssetId}`,
        method: 'DELETE',
      }),
    }),
    syncInspectionToCondition: build.mutation<void, SyncInspectionToConditionApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/sync/inspection-to-condition`,
        method: 'PUT',
        body: {inspectionId: queryArg.inspectionId},
      }),
      invalidatesTags: (_result, _error, queryArg) => [{type: 'Vehicle', id: queryArg.vehicleId}],
      extraOptions: {
        requestSchema: syncInspectionToConditionApiArgSchema,
      },
    }),
    cloneInspection: build.mutation<void, CloneInspectionApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/inspection/${queryArg.inspectionId}/clone`,
        method: 'POST',
      }),
    }),
    updateInspectionStructureToLatestVersion: build.mutation<void, UpdateInspectionStructureApiArg>(
      {
        query: (queryArg) => ({
          url: `/car-audit/v1/inspection/${queryArg.inspectionId}/structure-version/to-latest`,
          method: 'POST',
        }),
      }
    ),
    auditAssetsUploadV2: build.mutation<AuditAssetsUploadV2ApiResponse, AuditAssetsUploadV2ApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v2/audits/images/upload`,
        method: 'POST',
        body: queryArg.requestBody,
      }),
    }),
    updateAuditConditionData: build.mutation<
      UpdateAuditConditionDataApiResponse,
      UpdateAuditConditionDataApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}`,
        method: 'PUT',
        body: queryArg.requestBody,
      }),
    }),
    moveInspectionData: build.mutation<MoveInspectionDataApiResponse, MoveInspectionDataApiArg>({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}/inspection-data/move`,
        method: 'POST',
        body: queryArg.moveInspectionDataRequestBody,
      }),
    }),
    getInspectionEquipmentDocument: build.query<
      GetInspectionEquipmentDocumentApiResponse,
      GetInspectionEquipmentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}/equipment-document`,
        method: 'GET',
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'InspectionEquipmentDocument', id: queryArg.auditId},
      ],
      extraOptions: {
        responseSchema: getInspectionEquipmentDocumentApiResponseSchema,
      },
    }),
    replaceInspectionEquipmentDocument: build.mutation<
      ReplaceInspectionEquipmentDocumentApiResponse,
      ReplaceInspectionEquipmentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}/equipment-document`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: ReplaceInspectionEquipmentDocumentApiArg) => [
          {type: 'InspectionEquipmentDocument', id: queryArg.auditId},
        ]
      ),
    }),
    fileStorageUploadV2: build.mutation<FileStorageUploadV2ApiResponse, FileStorageUploadV2ApiArg>({
      query: () => ({
        url: `/car-audit/v2/file-storage/upload`,
        method: 'POST',
      }),
      extraOptions: {
        responseSchema: fileStorageUploadV2ApiResponseSchema,
      },
    }),
    removeInspectionEquipmentDocument: build.mutation<
      RemoveInspectionEquipmentDocumentApiResponse,
      RemoveInspectionEquipmentDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/car-audit/v1/audits/${queryArg.auditId}/equipment-document`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: RemoveInspectionEquipmentDocumentApiArg) => [
          {type: 'InspectionEquipmentDocument', id: queryArg.auditId},
        ]
      ),
    }),
  }),
});

export const selectAutoFillTemplates = carAuditApi.endpoints.getAutoFillTemplates.select();

export const {
  usePrefetch,
  useCreateAutoFillTemplateMutation,
  useGetAutoFillTemplatesQuery,
  useCreateAuditMutation,
  useDeleteAutoFillTemplateMutation,
  useGetNewestStructureByTypeQuery,
  useLazyGetNewestStructureByTypeQuery,
  useUpdateAuditDataMutation,
  useGetAuditStructureQuery,
  useLazyGetAuditStructureQuery,
  useGetAuditDataQuery,
  useLazyGetAuditDataQuery,
  useGetMultipleAuditDataQuery,
  useLazyGetMultipleAuditDataQuery,
  useLazyGetAutoFillTemplateQuery,
  useGetAutoFillTemplateQuery,
  useToFinishingInspectionMutation,
  useToRevisionInspectionMutation,
  useDeleteInspectionTemplateMutation,
  useGetInspectionTemplatesQuery,
  useLazyGetInspectionTemplatesQuery,
  useToggleInspectionTemplateActiveStateMutation,
  useGetBasicStructureForSettingsQuery,
  useCreateTypeOfControlSettingsMutation,
  useGetStructureSettingsQuery,
  useLazyGetPublicizeInspectionQuery,
  useDeleteInspectionMutation,
  useInspectionListQuery,
  useCreateInspectionMutation,
  useLoadAuditDataByVehicleIdQuery,
  useLazyLoadAuditDataByVehicleIdQuery,
  useGetSpecificallyAuditDataQuery,
  useRotateImageAssetMutation,
  useFileOperationListQuery,
  useGetVehicleAuditApiCatalogsQuery,
  useToInRevisionInspectionMutation,
  useInspectionToInProgressMutation,
  useLazyGetVideoAssetQuery,
  useUploadVideoAssetMutation,
  useDeleteVideoAssetMutation,
  useSyncInspectionToConditionMutation,
  useCloneInspectionMutation,
  useUpdateInspectionStructureToLatestVersionMutation,
  useAuditAssetsUploadV2Mutation,
  useUpdateAuditConditionDataMutation,
  useMoveInspectionDataMutation,
  useGetInspectionEquipmentDocumentQuery,
  useReplaceInspectionEquipmentDocumentMutation,
  useRemoveInspectionEquipmentDocumentMutation,
  useFileStorageUploadV2Mutation,
} = carAuditApi;
